import { useState, useEffect } from "react";
import { Equipment } from "../types/equipment";
import { EquipmentType } from "../types/equipment-type";
import { Store } from "../types/store";
import { Loader2, Filter, X, Search } from "lucide-react";
import toast from "react-hot-toast";
import { getEquipment } from "../services/equipmentApi";
import { getStores } from "../services/storeApi";
import { getTechnicianByEmail } from "../services/technicianApi";
import { getEquipmentTypes } from "../services/equipmentTypeApi";
import { useAuth } from "../hooks/useAuth";

export default function EquipmentPage() {
  const { user } = useAuth();
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // State for filters and tabs
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [storeFilter, setStoreFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [allocationFilter, setAllocationFilter] = useState("");
  const [currentTab, setCurrentTab] = useState<{
    type: number;
    subtype?: number;
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Initialization effect
  useEffect(() => {
    const initializeData = async () => {
      try {
        setIsLoading(true);
        const [typesResponse, storesResponse] = await Promise.all([
          getEquipmentTypes(),
          getStores(),
        ]);

        const loadedTypes = typesResponse.data.data;
        setEquipmentTypes(loadedTypes);
        setStores(storesResponse.data.data);

        // Set default tab to first type
        if (loadedTypes.length > 0) {
          setCurrentTab({ type: loadedTypes[0].id });
        }

        // Set default technician filter for non-admin users
        if (user?.email && user?.utype !== "ADMIN") {
          const techResponse = await getTechnicianByEmail(user.email);
          if (techResponse.data.data) {
            setAssignedToFilter(techResponse.data.data.id.toString());
          }
        }
      } catch (err) {
        console.error("Failed to initialize data:", err);
        toast.error("Failed to load initial data");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [user?.email, user?.utype]);

  // Filtering logic
  useEffect(() => {
    const fetchFilteredEquipment = async () => {
      try {
        const filters: any = {
          status: statusFilter || undefined,
          search: activeSearchQuery || undefined,
          store: storeFilter || undefined,
          assigned_to: assignedToFilter || undefined,
          page: currentPage,
          per_page: perPage,
        };

        // Determine type filtering
        if (currentTab) {
          // If subtype is selected, only filter by subtype
          if (currentTab.subtype) {
            filters.type = currentTab.subtype.toString();
            filters.is_subtype = true; // Add flag to indicate this is a subtype
          } else {
            // If only main type is selected, send just the type ID
            filters.type = currentTab.type.toString();
          }
        }

        // Handle allocation filter
        if (allocationFilter === "allocated") {
          filters.allocated = true;
        } else if (allocationFilter === "unallocated") {
          filters.allocated = false;
        }

        // Remove undefined filters
        Object.keys(filters).forEach((key) => {
          if (filters[key] === undefined) {
            delete filters[key];
          }
        });

        try {
          setIsLoading(true);
          const response = await getEquipment(filters);

          // More robust data extraction
          let equipmentData: Equipment[] = [];
          let totalPagesCount = 1;

          const extractEquipmentData = (data: any): Equipment[] => {
            return data.map((item: Equipment) => ({
              ...item,
              equipment_type: {
                ...item.equipment_type,
                subtypes: item.equipment_type?.subtypes || [],
              },
            }));
          };

          if (response.data && typeof response.data === "object") {
            if ("data" in response.data) {
              // Pagination response
              if (
                response.data.data.data &&
                Array.isArray(response.data.data.data)
              ) {
                equipmentData = extractEquipmentData(response.data.data.data);
                // Set total pages from meta data
                totalPagesCount = Math.ceil(
                  response.data.data.total / response.data.data.per_page
                );
              }
              // Direct data array
              else if (Array.isArray(response.data.data)) {
                equipmentData = extractEquipmentData(response.data.data);
              }
            }
          }

          setEquipment(equipmentData);
          setTotalPages(totalPagesCount);
        } catch (apiError) {
          console.error("API Call Error:", apiError);
          toast.error("Failed to fetch equipment from API");
          setEquipment([]);
        } finally {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Unexpected error in fetchFilteredEquipment:", error);
        toast.error("An unexpected error occurred");
        setEquipment([]);
        setIsLoading(false);
      }
    };

    fetchFilteredEquipment();
  }, [
    currentTab,
    statusFilter,
    activeSearchQuery,
    storeFilter,
    assignedToFilter,
    allocationFilter,
    equipmentTypes,
    currentPage,
    perPage,
  ]);

  // Update the renderEquipmentTypes function
  const renderEquipmentTypes = () => {
    return (
      <div className="mb-6">
        {/* Main Types */}
        <div className="flex flex-nowrap overflow-x-auto gap-2 pb-2 mb-2 scrollbar-hide">
          <button
            onClick={() => setCurrentTab(null)}
            className={`
                            flex-none px-4 py-2 rounded-lg font-medium text-sm
                            transition-all whitespace-nowrap
                            ${
                              !currentTab
                                ? "bg-indigo-600 dark:bg-indigo-500 text-white shadow-lg"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }
                        `}
          >
            All Equipment Types
          </button>
          {equipmentTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => handleTypeClick(type.id)}
              className={`
                                flex-none px-4 py-2 rounded-lg font-medium text-sm
                                transition-all whitespace-nowrap
                                ${
                                  currentTab?.type === type.id
                                    ? "bg-indigo-600 dark:bg-indigo-500 text-white shadow-lg"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }
                            `}
            >
              {type.name}
            </button>
          ))}
        </div>

        {/* Subtypes - Only show if a type is selected */}
        {currentTab?.type && (
          <div className="flex flex-wrap gap-2 mt-4 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg">
            <button
              onClick={() => handleSubtypeClick(currentTab.type!, undefined)}
              className={`
                                px-3 py-1.5 rounded-full text-sm font-medium
                                transition-all
                                ${
                                  !currentTab?.subtype
                                    ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-500 dark:border-indigo-400"
                                    : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                                }
                            `}
            >
              All {equipmentTypes.find((t) => t.id === currentTab.type)?.name}
            </button>
            {equipmentTypes
              .find((t) => t.id === currentTab?.type)
              ?.subtypes?.map((subtype) => (
                <button
                  key={subtype.id}
                  onClick={() =>
                    handleSubtypeClick(currentTab.type!, subtype.id)
                  }
                  className={`
                                        px-3 py-1.5 rounded-full text-sm font-medium
                                        transition-all
                                        ${
                                          currentTab?.subtype === subtype.id
                                            ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-500 dark:border-indigo-400"
                                            : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                                        }
                                    `}
                >
                  {subtype.name}
                </button>
              ))}
          </div>
        )}
      </div>
    );
  };

  // Render equipment list with mobile-friendly layout
  const renderEquipmentList = () => {
    if (equipment.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            No equipment found
          </p>
          <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
            Try adjusting your filters
          </p>
        </div>
      );
    }

    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {equipment.map((item) => (
          <div
            key={item.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-gray-700 overflow-hidden"
          >
            {/* Header Section */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {item.model_number}
                  </p>
                  <span className="text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full mt-1 inline-block">
                    {(() => {
                      const type = item.equipment_type?.name;
                      const subtype = item.equipment_type?.subtypes?.find(
                        (subtype) =>
                          subtype.id === Number(item.equipment_subtype_id) &&
                          subtype.equipment_type_id === item.equipment_type?.id
                      )?.name;
                      // console.log('Equipment:', {
                      //     item: item.name,
                      //     type,
                      //     equipment_subtype_id: item.equipment_subtype_id,
                      //     equipment_type_id: item.equipment_type?.id,
                      //     subtypes: item.equipment_type?.subtypes,
                      //     foundSubtype: subtype
                      // });
                      if (!type) return "Uncategorized";
                      return `${type} ${
                        subtype ? `• ${subtype}` : "• No Sub Type"
                      }`;
                    })()}
                  </span>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium
                                    ${
                                      item.status === "active"
                                        ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400"
                                        : item.status === "maintenance"
                                        ? "bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400"
                                        : "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-400"
                                    }`}
                >
                  {item.status}
                </span>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-4 space-y-3">
              {/* Serial Number */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Serial Number
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.serial_number || "N/A"}
                </span>
              </div>

              {/* Order Number */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Order Number
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {'#'+ item.order_number || "N/A"}
                </span>
              </div>

              {/* Allocation Status */}
              {item.allocated_id || item.store_number ? (
                <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <span className="text-sm text-green-700 dark:text-green-400">
                    Allocated to{" "}
                    {item.store_number
                      ? `Store #${item.store_number}`
                      : "Equipment"}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Assigned To
                  </span>
                  {item.user ? (
                    <span className="text-sm px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-full">
                      {item.user.name}
                    </span>
                  ) : (
                    <span className="text-sm px-2 py-1 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                      Not assigned
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Add this inside the EquipmentPage component
  const handleSubtypeClick = (typeId: number, subtypeId?: number) => {
    setCurrentTab((prev) => ({
      type: typeId,
      subtype: prev?.subtype === subtypeId ? undefined : subtypeId,
    }));
  };

  const handleTypeClick = (typeId: number) => {
    setCurrentTab((prev) =>
      prev?.type === typeId ? null : { type: typeId, subtype: undefined }
    );
  };

  // Add handleSearch function
  const handleSearch = () => {
    setActiveSearchQuery(searchQuery);
    setCurrentPage(1); // Reset to first page when searching
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-gray-500 dark:text-gray-400" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
        Equipment
      </h1>
      <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
        List of equipment that are currently available to be allocated to a job card.
      </p>

      {/* Mobile-Friendly Filters */}
      <div className="mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex justify-between items-center bg-gray-100 dark:bg-gray-700 p-3 rounded-lg text-gray-700 dark:text-gray-300"
          >
            <span>Filters</span>
            <Filter size={20} />
          </button>
          <button
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("");
              setStoreFilter("");
              setAssignedToFilter("");
              setAllocationFilter("");
              setCurrentTab(null);
            }}
            className="flex justify-between items-center bg-gray-100 dark:bg-gray-700 p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          >
            <span>Clear Filters</span>
            <X size={20} />
          </button>
        </div>
        {isFilterOpen && (
          <div className="space-y-4 mt-4">
            {/* Search Filter */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search by name, serial number, model number, or order number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  className="w-full p-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors duration-200 flex items-center gap-2"
              >
                <Search size={20} />
                <span>Search</span>
              </button>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full p-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>

            {/* Store Filter */}
            <select
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
              className="w-full p-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>

            {/* Allocation Filter */}
            <select
              value={allocationFilter}
              onChange={(e) => setAllocationFilter(e.target.value)}
              className="w-full p-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Allocations</option>
              <option value="allocated">Allocated</option>
              <option value="unallocated">Unallocated</option>
            </select>
          </div>
        )}
      </div>

      {/* Equipment Types */}
      <div className="mb-6 overflow-x-auto">{renderEquipmentTypes()}</div>

      {/* Equipment List */}
      <div>{renderEquipmentList()}</div>

      {/* Add pagination controls */}
      <div className="mt-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing items per page
            }}
            className="rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
